import { useCallback, useEffect, useMemo, useState } from 'react';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import PublishRoundedIcon from '@mui/icons-material/PublishRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Divider,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Slider,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useNotify } from 'react-admin';

import {
    createSemanticContentScoringPolicyDraft,
    getSemanticContentScoringPolicies,
    getSemanticContentScoringPolicy,
    isSemanticContentScoringPolicy,
    publishSemanticContentScoringPolicy,
    saveSemanticContentScoringPolicyDraft,
    validateSemanticContentScoringPolicy,
    type SemanticContentScoringAlternative,
    type SemanticContentScoringCondition,
    type SemanticContentScoringOperator,
    type SemanticContentScoringPolicy,
    type SemanticContentScoringPolicyListItem,
    type SemanticContentScoringTrait,
    type SemanticContentScoringValidationResult,
} from './semanticContentScoringPoliciesApi';

const operators: Array<{ value: SemanticContentScoringOperator; label: string }> = [
    { value: 'atLeast', label: 'At least' },
    { value: 'atMost', label: 'At most' },
    { value: 'equals', label: 'Exactly' },
    { value: 'notEquals', label: 'Not' },
];

const clone = <T,>(value: T): T => JSON.parse(JSON.stringify(value)) as T;
const variantKey = (item: Pick<SemanticContentScoringPolicyListItem, 'examName' | 'examTaskType'>) =>
    `${item.examName}:${item.examTaskType}`;
const variantLabel = (item: Pick<SemanticContentScoringPolicyListItem, 'examName' | 'examTaskType'>) =>
    `${item.examName} · ${item.examTaskType.replace(/([a-z])([A-Z])/g, '$1 $2')}`;

function selectedVariantPolicy(
    policies: SemanticContentScoringPolicyListItem[],
    key: string,
): SemanticContentScoringPolicyListItem | undefined {
    const candidates = policies.filter((item) => variantKey(item) === key && item.status !== 'retired');
    return candidates.find((item) => item.status === 'draft') ?? candidates.find((item) => item.status === 'published');
}

type ConditionEditorProps = {
    condition: SemanticContentScoringCondition;
    traits: SemanticContentScoringTrait[];
    editable: boolean;
    onChange: (condition: SemanticContentScoringCondition) => void;
    onRemove: () => void;
};

function ConditionEditor({ condition, traits, editable, onChange, onRemove }: ConditionEditorProps) {
    const trait = traits.find((item) => item.key === condition.traitKey) ?? traits[0];
    if (!trait) return null;
    const optionIndex = Math.max(0, trait.options.findIndex((option) => option.value === condition.value));
    const selectedOption = trait.options[optionIndex];
    const sliderMarks = trait.options.map((option, index) => ({ value: index, label: option.label }));

    if (!editable) {
        return (
            <Paper variant="outlined" sx={{ px: 2, py: 1.5, borderRadius: 2, bgcolor: '#fff' }}>
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: 'minmax(180px, 0.8fr) minmax(120px, 0.45fr) minmax(180px, 1fr)' },
                        gap: 1.5,
                        alignItems: 'center',
                    }}
                >
                    <Box>
                        <Typography variant="caption" color="text.secondary">Trait</Typography>
                        <Typography fontWeight={750}>{trait.label}</Typography>
                    </Box>
                    <Box>
                        <Typography variant="caption" color="text.secondary">Rule</Typography>
                        <Typography fontWeight={750}>
                            {operators.find((operator) => operator.value === condition.operator)?.label ?? condition.operator}
                        </Typography>
                    </Box>
                    <Box>
                        <Typography variant="caption" color="text.secondary">Required performance</Typography>
                        <Chip
                            label={selectedOption?.label ?? condition.value}
                            color="primary"
                            variant="outlined"
                            size="small"
                            sx={{ mt: 0.25, fontWeight: 750 }}
                        />
                    </Box>
                </Box>
            </Paper>
        );
    }

    return (
        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '220px 140px minmax(320px, 1fr) auto' },
                    gap: { xs: 1.5, md: 2 },
                    alignItems: 'center',
                }}
            >
                    <FormControl size="small" fullWidth>
                        <InputLabel>Trait</InputLabel>
                        <Select
                            label="Trait"
                            value={trait.key}
                            disabled={!editable}
                            onChange={(event) => {
                                const nextTrait = traits.find((item) => item.key === event.target.value) ?? trait;
                                onChange({
                                    traitKey: nextTrait.key,
                                    operator: nextTrait.options.length === 2 ? 'equals' : condition.operator,
                                    value: nextTrait.options[0]?.value ?? '',
                                });
                            }}
                        >
                            {traits.map((item) => (
                                <MenuItem key={item.key} value={item.key}>
                                    {item.label}{item.adminVisible ? '' : ' · advanced'}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" fullWidth>
                        <InputLabel>Rule</InputLabel>
                        <Select
                            label="Rule"
                            value={condition.operator}
                            disabled={!editable}
                            onChange={(event) => onChange({
                                ...condition,
                                operator: event.target.value as SemanticContentScoringOperator,
                            })}
                        >
                            {operators.map((operator) => (
                                <MenuItem key={operator.value} value={operator.value}>{operator.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                <Box sx={{ px: 1.5, minWidth: 0 }}>
                    <Slider
                        value={optionIndex}
                        min={0}
                        max={Math.max(0, trait.options.length - 1)}
                        step={1}
                        marks={sliderMarks}
                        valueLabelDisplay="auto"
                        valueLabelFormat={(value) => trait.options[Number(value)]?.label ?? ''}
                        onChange={(_event, value) => {
                            const nextIndex = Array.isArray(value) ? value[0] : value;
                            onChange({ ...condition, value: trait.options[nextIndex]?.value ?? condition.value });
                        }}
                    />
                </Box>
                <Button color="error" size="small" startIcon={<DeleteOutlineRoundedIcon />} onClick={onRemove}>
                    Remove
                </Button>
            </Box>
        </Paper>
    );
}

type AlternativeEditorProps = {
    alternative: SemanticContentScoringAlternative;
    traits: SemanticContentScoringTrait[];
    alternativeIndex: number;
    editable: boolean;
    canRemove: boolean;
    onChange: (alternative: SemanticContentScoringAlternative) => void;
    onRemove: () => void;
};

function AlternativeEditor({
    alternative,
    traits,
    alternativeIndex,
    editable,
    canRemove,
    onChange,
    onRemove,
}: AlternativeEditorProps) {
    const addCondition = () => {
        const unusedTrait = traits.find((trait) => !alternative.conditions.some((condition) => condition.traitKey === trait.key)) ?? traits[0];
        if (!unusedTrait) return;
        onChange({
            conditions: [
                ...alternative.conditions,
                {
                    traitKey: unusedTrait.key,
                    operator: unusedTrait.options.length === 2 ? 'equals' : 'atLeast',
                    value: unusedTrait.options[0]?.value ?? '',
                },
            ],
        });
    };

    return (
        <Paper sx={{ p: { xs: 1.5, md: 2 }, bgcolor: '#f8fafc', borderRadius: 2 }} variant="outlined">
            <Stack spacing={1.5}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                        <Typography fontWeight={850}>Complete descriptor {alternativeIndex + 1}</Typography>
                        <Typography variant="caption" sx={{ color: '#64748b' }}>
                            Every rule below must match. Separate descriptors are OR choices.
                        </Typography>
                    </Box>
                    {canRemove ? <Button color="error" size="small" onClick={onRemove}>Remove descriptor</Button> : null}
                </Stack>
                {alternative.conditions.map((condition, conditionIndex) => (
                    <ConditionEditor
                        key={`${condition.traitKey}:${conditionIndex}`}
                        condition={condition}
                        traits={traits}
                        editable={editable}
                        onChange={(nextCondition) => {
                            const conditions = [...alternative.conditions];
                            conditions[conditionIndex] = nextCondition;
                            onChange({ conditions });
                        }}
                        onRemove={() => onChange({
                            conditions: alternative.conditions.filter((_item, index) => index !== conditionIndex),
                        })}
                    />
                ))}
                {editable ? (
                    <Button size="small" startIcon={<AddRoundedIcon />} onClick={addCondition} sx={{ alignSelf: 'flex-start' }}>
                        Add trait rule
                    </Button>
                ) : null}
            </Stack>
        </Paper>
    );
}

export function SemanticContentScoringPoliciesPanel() {
    const notify = useNotify();
    const [items, setItems] = useState<SemanticContentScoringPolicyListItem[]>([]);
    const [selectedKey, setSelectedKey] = useState('');
    const [policy, setPolicy] = useState<SemanticContentScoringPolicy | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [validation, setValidation] = useState<SemanticContentScoringValidationResult | null>(null);

    const variants = useMemo(() => {
        const result = new Map<string, SemanticContentScoringPolicyListItem>();
        items.filter((item) => item.status !== 'retired').forEach((item) => {
            const key = variantKey(item);
            const current = result.get(key);
            if (!current || item.status === 'draft') result.set(key, item);
        });
        return [...result.values()].sort((left, right) => variantLabel(left).localeCompare(variantLabel(right)));
    }, [items]);

    const loadList = useCallback(async (preferredKey?: string) => {
        setLoading(true);
        setError('');
        try {
            const nextItems = await getSemanticContentScoringPolicies();
            setItems(nextItems);
            const firstPolicy = nextItems.find((item) => item.status === 'published') ?? nextItems[0];
            if (!firstPolicy) {
                setSelectedKey('');
                setPolicy(null);
                setValidation(null);
                return;
            }
            const nextKey = preferredKey || selectedKey || variantKey(firstPolicy);
            setSelectedKey(nextKey);
            const selected = selectedVariantPolicy(nextItems, nextKey);
            setPolicy(selected ? await getSemanticContentScoringPolicy(selected.semanticContentScoringPolicyId) : null);
            setValidation(null);
        } catch (loadError) {
            setError(loadError instanceof Error ? loadError.message : 'Unable to load semantic Content policies');
        } finally {
            setLoading(false);
        }
    }, [selectedKey]);

    useEffect(() => { void loadList(); }, []); // Initial database-backed policy load only.

    const chooseVariant = async (key: string) => {
        setSelectedKey(key);
        setLoading(true);
        setError('');
        try {
            const selected = selectedVariantPolicy(items, key);
            setPolicy(selected ? await getSemanticContentScoringPolicy(selected.semanticContentScoringPolicyId) : null);
            setValidation(null);
        } catch (loadError) {
            setError(loadError instanceof Error ? loadError.message : 'Unable to load semantic Content policy');
        } finally {
            setLoading(false);
        }
    };

    const updatePolicy = (mutate: (draft: SemanticContentScoringPolicy) => void) => {
        if (!policy || policy.status !== 'draft') return;
        const draft = clone(policy);
        mutate(draft);
        setPolicy(draft);
        setValidation(null);
    };

    const createDraft = async () => {
        if (!policy) return;
        setSaving(true);
        try {
            const draft = await createSemanticContentScoringPolicyDraft(policy.examName, policy.examTaskType);
            setPolicy(draft);
            await loadList(variantKey(draft));
            notify('Editable scoring draft created', { type: 'success' });
        } catch (draftError) {
            notify(draftError instanceof Error ? draftError.message : 'Unable to create draft', { type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const saveDraft = async () => {
        if (!policy || policy.status !== 'draft') return;
        setSaving(true);
        try {
            const result = await saveSemanticContentScoringPolicyDraft(policy);
            if (isSemanticContentScoringPolicy(result)) {
                setPolicy(result);
                setValidation(null);
                notify('Scoring draft saved', { type: 'success' });
            } else {
                setValidation(result);
                notify('Fix the policy validation errors before saving', { type: 'warning' });
            }
        } catch (saveError) {
            notify(saveError instanceof Error ? saveError.message : 'Unable to save draft', { type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const validateDraft = async () => {
        if (!policy) return;
        const result = await validateSemanticContentScoringPolicy(policy.semanticContentScoringPolicyId);
        setValidation(result);
        notify(result.isValid ? 'Policy is valid' : 'Policy has validation errors', { type: result.isValid ? 'success' : 'warning' });
    };

    const publishDraft = async () => {
        if (!policy || policy.status !== 'draft') return;
        if (!window.confirm(`Publish ${variantLabel(policy)} policy version ${policy.policyVersion}?`)) return;
        setSaving(true);
        try {
            const result = await publishSemanticContentScoringPolicy(
                policy.semanticContentScoringPolicyId,
                policy.lockVersion,
            );
            if (isSemanticContentScoringPolicy(result)) {
                setPolicy(result);
                setValidation(null);
                await loadList(variantKey(result));
                notify('Scoring policy published and activated', { type: 'success' });
            } else {
                setValidation(result);
            }
        } catch (publishError) {
            notify(publishError instanceof Error ? publishError.message : 'Unable to publish policy', { type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    if (loading && !policy) return <Box sx={{ py: 8, textAlign: 'center' }}><CircularProgress /></Box>;

    return (
        <Stack spacing={2.5}>
            <Alert severity="info">
                Each Content band is a complete holistic descriptor. Rules inside one descriptor are ANDed;
                separate descriptors are OR choices. Zero gates and the minimum Content 1 floor are locked.
            </Alert>
            <Alert severity="warning">
                Publishing changes learner scoring. Structural validation prevents malformed or contradictory tables;
                an authorized reviewer must still confirm every edited descriptor against the current task-specific rubric.
            </Alert>
            {error ? <Alert severity="error">{error}</Alert> : null}
            <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems={{ lg: 'center' }}>
                <FormControl size="small" sx={{ minWidth: 330 }}>
                    <InputLabel>Exam and task variant</InputLabel>
                    <Select label="Exam and task variant" value={selectedKey} onChange={(event) => void chooseVariant(event.target.value)}>
                        {variants.map((item) => <MenuItem key={variantKey(item)} value={variantKey(item)}>{variantLabel(item)}</MenuItem>)}
                    </Select>
                </FormControl>
                {policy ? <>
                    <Chip label={`V${policy.policyVersion}`} />
                    <Chip label={policy.status} color={policy.status === 'published' ? 'success' : 'warning'} />
                    <Chip label={`Maximum ${policy.maximumContentScore}`} variant="outlined" />
                </> : null}
                <Box sx={{ flex: 1 }} />
                <Button variant="outlined" startIcon={<RefreshRoundedIcon />} disabled={saving} onClick={() => void loadList(selectedKey)}>
                    Refresh
                </Button>
                {policy?.status === 'published' ? (
                    <Button variant="contained" startIcon={<EditRoundedIcon />} disabled={saving} onClick={createDraft}>Edit scoring rules</Button>
                ) : null}
                {policy?.status === 'draft' ? <>
                    <Button variant="outlined" disabled={saving} onClick={() => void validateDraft()}>Validate</Button>
                    <Button variant="outlined" startIcon={<SaveRoundedIcon />} disabled={saving} onClick={saveDraft}>Save draft</Button>
                    <Button variant="contained" color="success" startIcon={<PublishRoundedIcon />} disabled={saving} onClick={publishDraft}>Publish</Button>
                </> : null}
            </Stack>

            {policy ? <>
                {policy.status === 'published' ? (
                    <Alert
                        severity="info"
                        action={(
                            <Button color="inherit" size="small" startIcon={<EditRoundedIcon />} disabled={saving} onClick={createDraft}>
                                Edit
                            </Button>
                        )}
                    >
                        This is the active published policy. Create a draft to change its sliders or rules.
                    </Alert>
                ) : (
                    <Alert severity="warning">
                        You are editing a draft. Learner scoring will not change until this version is validated and published.
                    </Alert>
                )}
                <Card variant="outlined">
                    <CardContent>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                            <TextField
                                fullWidth
                                label="Rubric version"
                                value={policy.rubricVersion}
                                disabled={policy.status !== 'draft'}
                                onChange={(event) => updatePolicy((draft) => { draft.rubricVersion = event.target.value; })}
                            />
                            <TextField
                                fullWidth
                                label="Change summary"
                                value={policy.changeSummary}
                                disabled={policy.status !== 'draft'}
                                onChange={(event) => updatePolicy((draft) => { draft.changeSummary = event.target.value; })}
                            />
                        </Stack>
                    </CardContent>
                </Card>

                {validation ? (
                    <Alert severity={validation.isValid ? 'success' : 'error'}>
                        {validation.isValid ? 'Policy is valid and ready to publish.' : (
                            <Stack component="ul" sx={{ m: 0, pl: 2.5 }}>
                                {validation.errors.map((item, index) => (
                                    <Typography component="li" key={`${item.ruleCode}:${item.path}:${index}`} variant="body2">
                                        {item.message} ({item.path})
                                    </Typography>
                                ))}
                            </Stack>
                        )}
                    </Alert>
                ) : null}

                {policy.configuration.bands.map((band, bandIndex) => (
                    <Card key={band.score} variant="outlined" sx={{ borderRadius: 3 }}>
                        <CardContent>
                            <Stack spacing={2}>
                                <Stack direction="row" alignItems="center" justifyContent="space-between">
                                    <Box>
                                        <Typography variant="h6" fontWeight={900}>Content {band.score}</Typography>
                                        <Typography variant="body2" sx={{ color: '#64748b' }}>
                                            {band.score === 1 ? 'Locked fallback for an assessable response.' : 'Highest matching band wins.'}
                                        </Typography>
                                    </Box>
                                    <Chip label={`${band.alternatives.length} descriptor${band.alternatives.length === 1 ? '' : 's'}`} />
                                </Stack>
                                <Divider />
                                {band.score === 1 ? <Alert severity="info">No slider is allowed here. Content 0 remains controlled only by the zero gate.</Alert> : (
                                    <>
                                        {band.alternatives.map((alternative, alternativeIndex) => (
                                            <AlternativeEditor
                                                key={alternativeIndex}
                                                alternative={alternative}
                                                traits={policy.configuration.traits}
                                                alternativeIndex={alternativeIndex}
                                                editable={policy.status === 'draft'}
                                                canRemove={policy.status === 'draft' && band.alternatives.length > 1}
                                                onChange={(nextAlternative) => updatePolicy((draft) => {
                                                    draft.configuration.bands[bandIndex].alternatives[alternativeIndex] = nextAlternative;
                                                })}
                                                onRemove={() => updatePolicy((draft) => {
                                                    draft.configuration.bands[bandIndex].alternatives.splice(alternativeIndex, 1);
                                                })}
                                            />
                                        ))}
                                        {policy.status === 'draft' ? (
                                            <Button
                                                size="small"
                                                startIcon={<AddRoundedIcon />}
                                                onClick={() => updatePolicy((draft) => {
                                                    const source = draft.configuration.bands[bandIndex].alternatives[0];
                                                    draft.configuration.bands[bandIndex].alternatives.push(clone(source));
                                                })}
                                                sx={{ alignSelf: 'flex-start' }}
                                            >
                                                Add OR descriptor
                                            </Button>
                                        ) : null}
                                    </>
                                )}
                            </Stack>
                        </CardContent>
                    </Card>
                ))}
            </> : <Alert severity="warning">No policy exists for this variant.</Alert>}
        </Stack>
    );
}
